import { LayerSpecification } from "maplibre-gl";
import { MaplibreLayer, RoutingControl } from "mobility-toolbox-js/ol";
import { Map } from "ol";
import Feature from "ol/Feature";
import { Geometry } from "ol/geom";
import { defaults as defaultInteractions } from "ol/interaction";
import { Layer } from "ol/layer";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { shared as sharedIconImageCache } from "ol/style/IconImageCache";
import OLEEditor from "ole/src/editor";

import GraphhopperRoutingControl, {
  GraphhopperRoutingProfile,
} from "./components/Editor/GraphhopperRoutingControl";
import configs, { ConfigId } from "./config";
import layouts from "./config/layouts";
import envConfigApp from "./envConfigApp";
import envConfigIo from "./envConfigIo";
import aerial from "./images/aerial.png";
import netzkarte from "./images/netzkarte.png";
import saturiert from "./images/saturiert.png";
import { BaseConfig, IndustryEnum, Me, User } from "./types/api-schema";
import { createBaseLayers, getCurrentBaseLayer } from "./utils";
import {
  AERIAL_LAYER_NAME,
  BASE_AERIAL_MAPSET_STYLE,
  BASE_BRIGHT_NETZKARTE_MAPSET_STYLE,
  BASE_BRIGHT_NETZKARTE_SATURIERT_MAPSET_STYLE,
  BASE_LAYER_NAME,
  BASE_LAYER_PARAM,
  Domain,
  DRAW_TYPE_GROUP_AREA,
  DRAW_TYPE_GROUP_LINE,
  DRAW_TYPE_GROUP_POINT,
  DRAW_TYPE_ICON,
  DRAW_TYPE_LINE,
  DRAW_TYPE_POLYGON,
  HEADER_HEIGHT,
  LS_SNAPPING_TYPE,
  MAPBOX_STYLELAYERS,
  MAPSET_METADATA_BUS_VALUE,
  MAPSET_METADATA_FILTER,
  MAPSET_METADATA_TRAM_VALUE,
  Mot,
  OL_ICON_CACHE_SIZE,
  OTHER_MOTS,
  PREVIEW_PATHNAME,
  RoutingService,
  SATURATED_LAYER_NAME,
  SaveStatus,
  SNAP_CONTROL_LINES,
  SNAP_CONTROL_OFF,
  SNAP_CONTROL_POINTS,
  SnappingType,
  STYLE_REVIEW_PREFIX,
} from "./utils/constants";
import createEditLayer from "./utils/createEditLayer";
import createInspectLayer from "./utils/createInspectLayer";
import createRoutingControl from "./utils/createRoutingControl";
import createSearchHighlightLayer from "./utils/createSearchHighlightLayer";
import getEnvFromStorage from "./utils/getEnvFromStorage";
import getEnvParent from "./utils/getEnvParent";
import getEnvUrlsAndKeys, { EnvUrlsAndKeys } from "./utils/getEnvUrlsAndKeys";
import { getGlobalIconsByDomain } from "./utils/getGlobalIconsByDomain";
import getRoutingLayer from "./utils/getRoutingLayer";
import LayerService from "./utils/LayerService";
import {
  createMaplibreStyleLayer,
  setMaplibreLayer,
} from "./utils/maplibreStyle";

import type { RoutingMot } from "mobility-toolbox-js/types";

import type {
  IconCategory,
  KmlHistory,
  LanguageOption,
  Layout,
  LayoutConfig,
  MetaKml,
  Organization,
  ParentOptions,
  PlanInfo,
} from "./model/app/propTypes";

// [MAPSETW-477] Increase cache size of icons
sharedIconImageCache.setSize(OL_ICON_CACHE_SIZE);

const initialWidth = 350;
const { VITE_ANONYMOUS_USER_TOKEN, VITE_ENV, VITE_SENTRY_RELEASE } = import.meta
  .env;

/** Read local storage to initialize user preferences */
const { routingDefaultMot: lastRoutingMotUsed, ...envFromStorage } =
  getEnvFromStorage();

const snappingTypeOptions = [
  SNAP_CONTROL_LINES,
  SNAP_CONTROL_POINTS,
  SNAP_CONTROL_OFF,
];
const lsSnappingType = localStorage.getItem(LS_SNAPPING_TYPE);
const snappingType: SnappingType =
  lsSnappingType && snappingTypeOptions.includes(lsSnappingType as SnappingType)
    ? (lsSnappingType as SnappingType)
    : SNAP_CONTROL_POINTS;

const maplibreStyleLayers = MAPBOX_STYLELAYERS.map((styleLayer) => {
  return createMaplibreStyleLayer(styleLayer);
});

export const defaultBaseLayers = [
  {
    key: "ch.sbb.netzkarte",
    name: BASE_LAYER_NAME,
    style: `${STYLE_REVIEW_PREFIX}${BASE_BRIGHT_NETZKARTE_MAPSET_STYLE}`,
    thumbnail: netzkarte,
  },
  {
    key: "ch.sbb.netzkarte_saturiert",
    name: SATURATED_LAYER_NAME,
    style: `${STYLE_REVIEW_PREFIX}${BASE_BRIGHT_NETZKARTE_SATURIERT_MAPSET_STYLE}`,
    thumbnail: saturiert,
  },
  {
    key: "world.geops.aerial_sbb",
    name: AERIAL_LAYER_NAME,
    style: `${STYLE_REVIEW_PREFIX}${BASE_AERIAL_MAPSET_STYLE}`,
    thumbnail: aerial,
  },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConfigColor = {
  border?: string;
  fill: [number, number, number, number];
  hex?: string;
  name: string;
};

export type ConfigColors = {
  colors: ConfigColor[];
  iconGeneratorColors: ConfigColor[];
  lineColors: ConfigColor[];
  polygonFillColors: ConfigColor[];
  polygonLineColors: ConfigColor[];
  textBgColors: ConfigColor[];
};

export const SettingKey = {
  MocoIsActiveOnImport: "moco-is_active_on_import",
  MocoPublicationGeometrySources: "moco-publication_geometry_sources",
  MocoReferenceGraph: "moco-reference_graph",
  MocoRisApiConsumerHintParams: "moco-ris_api_consumer_hint_params",
  MocoSetActiveForOwnerRefs: "moco-set_active_for_owner_refs",
  StopsApiBbox: "stops_api_bbox",
  StopsApiPrefagencies: "stops_api_prefagencies",
  StopsMots: "stops_mots",
  Styles: "styles",
  TralisLineTags: "tralis-line_tags",
  // TODO: Get an exhaustive list of all setting keys used in the application.
} as const;

export type SettingsKey = (typeof SettingKey)[keyof typeof SettingKey];

export type Settings = Partial<Record<SettingsKey, string>>;

/**
 * A configuration entry from `src/config/*` describing a customer/domain
 * (SBB, CH, IO, ...). The exact shape varies per config module, so we only
 * type the properties consumed by this file and by common consumers.
 */
export type AppDomainConfig = {
  [key: string]: unknown;
  allowedParentApplications?: string;
  baseLayers?: BaseLayerDescriptor[];
  domains?: Domain[];
  font?: string;
  getParentOptions?: (_parentUrl: string) => ParentOptions;
  iconCategories?: IconCategory[];
  iconSet?: string[];
  id: ConfigId;
  industry?: IndustryEnum;
  name?: string;
  settings?: Settings;
} & ConfigColors &
  Omit<BaseConfig, "settings">;

export interface BaseLayerDescriptor {
  key: string;
  name: string;
  style: string;
  thumbnail: string;
  url?: string;
  visible?: boolean;
}
export interface BusFilter {
  lines: string;
  provider: string;
}

export interface DialogPosition {
  x: number;
  y: number;
}

export interface DialogSize {
  height: number | string;
  width: number | string;
}

export interface ZoomLimits {
  maxZoom: number;
  minZoom?: number;
}

/**
 * Feature filter predicate used for bus / tram style layer filtering.
 * Kept intentionally loose because OL feature typings vary across layers.
 */
export type FeatureFilter = (_feature: {
  [key: string]: unknown;
  layer?: { metadata?: Record<string, unknown> };
}) => boolean;

export type ExportSize = [number, number];
export const ExportType = {
  Image: "image",
  Kml: "kml",
  Pdf: "pdf",
} as const;
export type ExportType = (typeof ExportType)[keyof typeof ExportType];

export type AppState = {
  [key: string]: unknown;
  activeButton?: string;
  anonymousUserToken: string;
  apiUrl: string;
  busFilter?: FeatureFilter;
  busFilters: BusFilter[];
  busLayers?: LayerSpecification[];
  bussesStyleUrl: string;
  canAnonUseExport?: boolean;
  canAnonUseRouteFinder?: boolean;
  config: AppDomainConfig;
  configs: AppDomainConfig[];
  countAnonUse?: Record<string, number>;
  currentGroupControls: Record<string, string>;
  currentSidebarWidth: number;
  defaultLang: string;
  dialogData?: null | Record<string, unknown>;
  dialogPosition: DialogPosition;
  dialogSize: DialogSize;
  dialogVisible: false | string;
  domain: Domain;
  drawInfo?: null | PlanInfo;
  drawInfoError: { name: string };
  drawInfos: PlanInfo[];
  drawInfosCount?: number;
  drawInfosNext?: null | string;
  drawInfosPrevious?: null | string;
  editor?: OLEEditor;
  exportBboxLockedToMap: boolean;
  exportBtnActive?: boolean;
  exportCoordinates?: null | number[][];
  exportFormat: string;
  exportLayout?: Layout;
  exportLayoutConfig: LayoutConfig;
  exportLayouts: Layout[];
  exportLayoutsDefaultOrg: string;
  exportNorthArrow: boolean;
  exportQrCode?: number;
  exportScale: number;
  exportSelect: boolean;
  exportSize: ExportSize | null;
  exportType?: ExportType;
  feedbackUrl: string;
  fetchError: unknown;
  geoapifyKey: string;
  geolocationActive?: boolean;
  globalIcons?: IconCategory[];
  graphhopperKey: string;
  hideSbbCopyright: boolean;
  iconCategories?: IconCategory[];
  iconsGroup?: Feature[];
  iconsSearch?: boolean;
  iconsUrl: string;
  imagesUrl: string;
  initialFilters?: BusFilter[];
  inspectModeActive: boolean;
  isAppReady?: boolean;
  isDialogPinToRight: boolean;
  isDrawing?: boolean;
  isKmlReady?: boolean;
  isReadOnlyMode?: boolean;
  isSidebarOpen: boolean;
  kmlAdminId?: null | string;
  kmlData?: string;
  kmlHistory: KmlHistory;
  kmlReadId?: null | string;
  kmlUrl: string;
  languageOptions: LanguageOption[];
  layoutsUrl: string;
  linkShortenerUrl: string;
  loginUrl: string;
  logoutUrl: string;
  mapsetEditorUrl: string;
  mapsetUrl: string;
  metaKml?: MetaKml;
  meUrl: string;
  oldKmlUrl: string;
  organization?: Organization;
  organizations?: Organization[];
  parentOptions?: ParentOptions;
  pictosPrefix: string;
  pictosUrl: string;
  prefAgencies: string;
  previewPathname: string;
  previousDialog?: false | null | string;
  pwaActive: boolean;
  routingBarrierFree?: boolean;
  routingDefaultMot?: GraphhopperRoutingProfile | RoutingMot;
  routingKey: string;
  routingMaxNbViaPoints?: number;
  routingMots: Mot[];
  routingService?: RoutingService;
  routingServiceByMot?: Partial<Record<Mot, RoutingService>>;
  routingUrl: string;
  saveConfirm?: SaveStatus;
  selectedFeature?: Feature;
  selectedFeatures?: Feature[];
  selectedPlan: string;
  shortenedReadLinks?: Record<string, string>;
  shortenedWriteLinks?: Record<string, string>;
  ssoClientId: string;
  ssoConfigsUrl: string;
  ssoOpenIdUrl: string;
  ssoRenewInterval: number;
  stopFinderKey: string;
  stopFinderUrl: string;
  thumbnailsUrl: string;
  tilesKey: string;
  tilesUrl: string;
  tramFilter?: FeatureFilter;
  useGeneralizedRouting: boolean;
  userInfoUrl: string;
  usingHistory?: boolean;
} & EnvUrlsAndKeys;

export interface MapState {
  /** Allow additional runtime properties during the JS → TS migration. */
  [key: string]: unknown;

  baseLayer?: MaplibreLayer;
  center: [number, number];
  defaultBaseLayers: BaseLayerDescriptor[];
  extent?: [number, number, number, number];
  layers: Layer[];
  layerService?: LayerService;
  olMap?: Map;
  resolution?: number;
  rotation: number;
  routingControl?: GraphhopperRoutingControl | RoutingControl;
  routingLayer?: VectorLayer<VectorSource<Feature<Geometry>>>;
  snappingType: SnappingType;
  styleLayers: Layer[];
  useGeneralizationLevels: boolean;
  zoom: number;
  zoomLimits: ZoomLimits;
}

/** Shape of the `oidc` slice contributed by `redux-oidc`. */
export interface OidcState {
  authComplete?: boolean;
  broadcast?: BroadcastChannel;
  me?: Me | null;
  user?: User;
}

/** Sentry initialization options as consumed by `@sentry/react`. */
export interface SentryConfig {
  autoSessionTracking: boolean;
  dsn: string;
  environment: string | undefined;
  release: string | undefined;
}

export interface EnvState {
  app: AppState;
  map: MapState;
  oidc: OidcState;
  sentry: SentryConfig;
}

export interface EnvStateByDomain {
  app: Partial<AppState>;
  map: Partial<MapState>;
}

// dev or stag or prod
export const env = VITE_ENV || "prod";
const envUrlsAndKeys = await getEnvUrlsAndKeys(env);
const domain = envUrlsAndKeys.domain;

// append special values depending on domain
const envByDomain = {
  app: envConfigApp,
  ch: {
    app: {},
    map: {},
  },
  io: envConfigIo,
};
const appByDomain = envByDomain[domain].app ?? {};
const mapByDomain = envByDomain[domain].map ?? {};

// Define the default state object
const state: EnvState = {
  app: {
    anonymousUserToken: VITE_ANONYMOUS_USER_TOKEN || "",
    ...envUrlsAndKeys,
    ...envFromStorage,
    busFilter: (feature) => {
      return (
        feature.layer?.metadata &&
        feature.layer.metadata[MAPSET_METADATA_FILTER] ===
          MAPSET_METADATA_BUS_VALUE
      );
    },
    busFilters: [{ lines: "", provider: "" }],
    config: configs.ch,
    configs: [],
    // ssoClientId: '000326',
    currentGroupControls: {
      [DRAW_TYPE_GROUP_AREA]: DRAW_TYPE_POLYGON,
      [DRAW_TYPE_GROUP_LINE]: DRAW_TYPE_LINE,
      [DRAW_TYPE_GROUP_POINT]: DRAW_TYPE_ICON,
    },

    /* SSO api configuration */
    currentSidebarWidth: 0, // at this time we do not know if the sidebar is drawn or not.
    defaultLang: "de-CH",
    dialogPosition: {
      x: document.body.clientWidth - (initialWidth + 10),
      y: HEADER_HEIGHT + 10,
    },
    dialogSize: {
      height: "auto",
      width: initialWidth,
    },

    domain,

    drawInfoError: null,

    drawInfos: [],

    exportBboxLockedToMap: false,

    exportLayoutConfig: {},

    exportLayouts: layouts,

    exportLayoutsDefaultOrg: "default",

    exportNorthArrow: true,

    exportScale: 1,

    exportSelect: false,

    exportSize: null,

    fetchError: null,
    globalIcons: getGlobalIconsByDomain(domain),

    // Force to hide sbb copyright, theoretically it depend on which base layer we use.
    // TODO move this to the base layer configuration
    hideSbbCopyright: false,
    inspectModeActive: false,
    isDialogPinToRight: false,
    isSidebarOpen: false,
    kmlHistory: {
      future: [],
      past: [],
      present: undefined,
    },
    languageOptions: [
      { child: "Deutsch", id: "de", value: "de-CH" },
      { child: "Français", id: "fr", value: "fr" },
      { child: "Italiano", id: "it", value: "it" },
      { child: "English", id: "en", value: "en" },
    ],
    organizations: [],

    prefAgencies: "sbb",

    previewPathname: PREVIEW_PATHNAME,

    /* App configuration */
    pwaActive: false,

    routingBarrierFree: true,
    routingDefaultMot: "foot",
    routingMots: ["foot", "bus", "rail", "car", "bike", ...OTHER_MOTS],
    routingService: RoutingService.Geops,
    routingServiceByMot: {
      bike: RoutingService.Graphhopper,
      car: RoutingService.Graphhopper,
    },

    selectedPlan: null,
    /* SSO url configuration */
    ssoClientId: "576922",

    ssoRenewInterval: 480000,

    tramFilter: (feature) => {
      return (
        feature.layer?.metadata &&
        feature.layer.metadata[MAPSET_METADATA_FILTER] ===
          MAPSET_METADATA_TRAM_VALUE
      );
    },

    // Recalculate the routing route when the zoom changes, it depends if bacground layers manage generalization or not.
    // TODO move this to the base layer configuration
    useGeneralizedRouting: true,
    ...appByDomain,
  },
  map: {
    center: [950401.28, 6004080.8],
    defaultBaseLayers,
    layers: [],
    rotation: 0,
    routingLayer: getRoutingLayer(),
    snappingType,
    styleLayers: maplibreStyleLayers.filter((styleLayer) => {
      const domains = styleLayer.get("domains");
      return domains ? domains.includes(domain) : true;
    }),
    useGeneralizationLevels: true,
    zoom: 15,
    zoomLimits: {
      maxZoom: 20,
    },
    ...mapByDomain,
  },
  oidc: {},
  sentry: {
    autoSessionTracking: false,
    dsn: "https://f88b48d351f24ecaba4e2ddd1b5e8e6b@sentry.geops.de/38",
    environment: env,
    release: VITE_SENTRY_RELEASE,
  },
};

// Parent parameter management
const parameters = new URLSearchParams(window.location.search);

const envParent = getEnvParent(parameters);
if (envParent) {
  Object.assign(state.app, envParent);
}

/* Routing configuration */
if (
  state.app.routingMots.some((mot: GraphhopperRoutingProfile | RoutingMot) => {
    return mot === lastRoutingMotUsed;
  })
) {
  state.app.routingDefaultMot = lastRoutingMotUsed;
} else {
  state.app.routingDefaultMot =
    state.app.routingDefaultMot || state.app.routingMots[0] || "foot";
}

state.map.routingControl = createRoutingControl(state);

/* Layer service configuration */
// Creates new base layers.
const baseLayers = createBaseLayers(
  [...(state.app.config.baseLayers || state.map.defaultBaseLayers)],
  state.app,
);

const layers = [
  ...state.map.styleLayers,
  createEditLayer(),
  createInspectLayer(),
  createSearchHighlightLayer(),
];

state.map.layerService = new LayerService(layers);

// We read the base layer permalink param to avoid unecessary re render when page loads.
state.map.baseLayer = getCurrentBaseLayer(
  baseLayers,
  parameters.get(BASE_LAYER_PARAM),
);

state.map.layers = [...baseLayers, ...layers];

// Apply the current base layer to all the style layers.
state.map.styleLayers.forEach((styleLayer) => {
  return setMaplibreLayer(styleLayer, state.map.baseLayer);
});

state.map.olMap = new Map({
  controls: [],
  interactions: defaultInteractions(),
});

state.map.olMap.addLayer(state.map.routingLayer);

export default state;
